import { enumDoNotBuyStatus } from '@rematter/frontend-lib-api-client';
import {
  Button,
  Col,
  Menu,
  Row,
  SearchField,
  TinyTable,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { cn } from '@rematter/ui-web/lib';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  LucideBuilding,
  LucideCalendar,
  LucideEllipsis,
  LucideMessageSquare,
  LucidePlusCircle,
  LucideShieldBan,
  LucideSquarePen,
  LucideTrash,
  LucideUser,
} from 'lucide-react';

import { DoNotBuyEntryRecord } from '../../_graphql/queryDoNotBuyEntries';
import * as doNotBuyState from '../../_state/doNotBuy.state';
import { useTranslations } from '../../i18n/useTranslations';
import { SettingsSubmodule } from '../_layouts/SettingsSubmodule';
import { SettingsContent } from '../_shared/SettingsContent';

import { DoNotBuyFormDialog } from './DoNotBuyFormDialog';

type DNBRowMenuProps = { row: DoNotBuyEntryRecord };

const DNBRowMenu = (props: DNBRowMenuProps) => {
  const t = useTranslations();
  const setEditingEntry = useSetAtom(doNotBuyState.$editingDoNotBuyEntry);
  const setDialogOpen = useSetAtom(doNotBuyState.$isDoNotBuyDialogOpen);
  const deleteEntry = useSetAtom(doNotBuyState.$deleteDoNotBuy);
  const toast = useToast();

  const handleDelete = async () => {
    try {
      await deleteEntry({ id: props.row.id });
      toast.success(t.complianceView.doNotBuy.dialog.messages.deleteSuccess);
    } catch (error) {
      toast.error(
        t.complianceView.doNotBuy.dialog.messages.deleteError(
          error instanceof Error ? error.message : 'Unknown error',
        ),
      );
    }
  };

  return (
    <div className="@DNBRowMenu contents">
      <Menu
        trigger={
          <Button
            className="px-2"
            size="small"
            color="neutral"
            variant="outline"
            icon={<LucideEllipsis />}
          />
        }
        items={[
          {
            label: t.complianceView.common.actions.edit,
            icon: <LucideSquarePen />,
            onAction: () => {
              setEditingEntry(props.row.id);
              setDialogOpen(true);
            },
          },
          {
            label: t.complianceView.common.actions.delete,
            icon: <LucideTrash />,
            className: 'text-failure',
            secondStep: ({ close }) => (
              <Col gap3>
                <div className="text-center text-sm text-neutral/70">
                  {t.complianceView.common.confirmDelete}
                </div>
                <Row gap2>
                  <Button variant="outline" color="neutral" onPress={close}>
                    {t.complianceView.common.actions.cancel}
                  </Button>
                  <Button
                    variant="outline"
                    color="failure"
                    icon={<LucideTrash />}
                    onPress={() => {
                      close();
                      handleDelete();
                    }}
                  >
                    {t.complianceView.common.yesDelete}
                  </Button>
                </Row>
              </Col>
            ),
          },
        ]}
      />
    </div>
  );
};

export const DoNotBuyListView = () => {
  const t = useTranslations();
  const entries = useAtomValue(doNotBuyState.$doNotBuyEntries);
  const [searchTerm, setSearchTerm] = useAtom(
    doNotBuyState.$doNotBuySearchTerm.value,
  );
  const setDialogOpen = useSetAtom(doNotBuyState.$isDoNotBuyDialogOpen);
  const setEditingEntry = useSetAtom(doNotBuyState.$editingDoNotBuyEntry);

  return (
    <Col className="@DoNotBuyListView h-full">
      <SettingsSubmodule
        title={t.complianceView.doNotBuy.title}
        subtitle={t.complianceView.doNotBuy.subtitle}
        tabs={null}
      >
        <SettingsContent className="h-full">
          <Col gap4 className="h-full">
            <Row gap2>
              <SearchField
                value={searchTerm}
                onChange={setSearchTerm}
                autoFocus
                placeholder={t.complianceView.doNotBuy.search.placeholder}
              />
              <Button
                className="ml-auto"
                icon={<LucidePlusCircle />}
                onPress={() => {
                  setEditingEntry(null);
                  setDialogOpen(true);
                }}
              >
                {t.complianceView.doNotBuy.actions.addEntry}
              </Button>
            </Row>
            <DoNotBuyFormDialog />
            <TinyTable
              rowKey={(row) => row.id}
              isLoading={entries.isLoading}
              error={entries.error}
              columns={[
                {
                  key: 'account',
                  label: t.complianceView.doNotBuy.columns.account,
                  icon: LucideBuilding,
                  cell: (row) => (
                    <div className="truncate font-medium">
                      {row.accountName ?? row.accountId}
                    </div>
                  ),
                },
                {
                  key: 'reason',
                  label: t.complianceView.doNotBuy.columns.reason,
                  icon: LucideMessageSquare,
                  width: 250,
                  cell: (row) => <div className="truncate">{row.reason}</div>,
                },
                {
                  key: 'status',
                  label: t.complianceView.doNotBuy.columns.status,
                  icon: LucideShieldBan,
                  width: 100,
                  cell: (row) => (
                    <span
                      className={cn(
                        row.status === enumDoNotBuyStatus.Active &&
                          'text-failure',
                        row.status !== enumDoNotBuyStatus.Active &&
                          'text-neutral/50',
                      )}
                    >
                      {row.status}
                    </span>
                  ),
                },
                {
                  key: 'addedBy',
                  label: t.complianceView.doNotBuy.columns.addedBy,
                  icon: LucideUser,
                  width: 150,
                  cell: (row) => (
                    <div className="truncate">{row.addedByUserName ?? '–'}</div>
                  ),
                },
                {
                  key: 'effectiveFrom',
                  label: t.complianceView.doNotBuy.columns.effectiveFrom,
                  icon: LucideCalendar,
                  width: 150,
                  cell: (row) => (
                    <div className="truncate">
                      {row.effectiveFrom
                        ? new Date(row.effectiveFrom).toLocaleDateString(
                            undefined,
                            {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            },
                          )
                        : '–'}
                    </div>
                  ),
                },
                {
                  key: 'actions',
                  label: null,
                  preventRowClick: true,
                  width: 'max-content',
                  cell: (row) => <DNBRowMenu row={row} />,
                },
              ]}
              data={entries.data ?? []}
            />
          </Col>
        </SettingsContent>
      </SettingsSubmodule>
    </Col>
  );
};
